import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  getContactMessage,
  updateContactStatus,
  type ContactDetail,
  type ContactStatus,
} from "@admin/lib/api";
import { formatDate } from "@admin/lib/utils";
import { SignupContextSection } from "@admin/components/SignupContextSection";
import { Card, CardContent, CardHeader, CardTitle } from "@admin/components/ui/card";
import { Button } from "@admin/components/ui/button";
import { cn } from "@admin/lib/utils";

function statusClass(status: ContactStatus): string {
  return status === "resolved"
    ? "bg-ok/20 text-ok"
    : "bg-warn/20 text-warn";
}

export default function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [item, setItem] = useState<ContactDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!id) return;
    getContactMessage(id)
      .then(setItem)
      .catch(() => setErr("Contact message not found"));
  }, [id]);

  const setStatus = async (status: ContactStatus) => {
    if (!id) return;
    setBusy(true);
    setErr(null);
    try {
      const updated = await updateContactStatus(id, status);
      setItem(updated);
    } catch {
      setErr("Could not update status");
    } finally {
      setBusy(false);
    }
  };

  if (err && !item) {
    return (
      <div className="space-y-2">
        <p className="text-danger">{err}</p>
        <Link to="/contact" className="text-sm text-accent hover:underline">
          Back to contact
        </Link>
      </div>
    );
  }
  if (!item) return <p className="text-muted-fg">Loading...</p>;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link to="/contact" className="text-sm text-accent hover:underline">
            Contact
          </Link>
          <h1 className="mt-2 text-2xl font-semibold">{item.email}</h1>
          <p className="text-sm text-muted-fg">
            {item.id} - received {formatDate(item.createdAt)}
          </p>
        </div>
        <div className="flex gap-2">
          {item.status === "pending" ? (
            <Button size="sm" disabled={busy} onClick={() => void setStatus("resolved")}>
              Mark resolved
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => void setStatus("pending")}
            >
              Mark pending
            </Button>
          )}
        </div>
      </div>

      {err && <p className="text-danger">{err}</p>}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Message</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex flex-wrap gap-2">
            <span className="w-24 shrink-0 text-muted-fg">Status</span>
            <span
              className={cn(
                "inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize",
                statusClass(item.status),
              )}
            >
              {item.status}
            </span>
          </div>
          {item.name && (
            <div className="flex flex-wrap gap-2">
              <span className="w-24 shrink-0 text-muted-fg">Name</span>
              <span>{item.name}</span>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <span className="w-24 shrink-0 text-muted-fg">Locale</span>
            <span className="uppercase">{item.locale}</span>
          </div>
          {item.resolvedAt && (
            <div className="flex flex-wrap gap-2">
              <span className="w-24 shrink-0 text-muted-fg">Resolved</span>
              <span>{formatDate(item.resolvedAt)}</span>
            </div>
          )}
          <div className="whitespace-pre-wrap rounded-lg border border-border bg-muted/30 p-4">
            {item.message}
          </div>
        </CardContent>
      </Card>

      <SignupContextSection metadata={item} />
    </div>
  );
}
